import { useEffect, useMemo, useState } from "react";
import { fetchWisdomEduEnvDashboard } from "services/wisdom/wisdomData";

// Six fixed source columns from the design. `titles` must match
// employee.employee.role.title EXACTLY as returned by the API (case-insensitive).
// Add every alias you actually see in your data to the relevant array.
const SOURCES = [
  { key: "acad", label: "المدير الأكاديمي", short: "أكاديمي", color: "#8B7CE8", titles: ["Academic Manager", "Academic Director", "Academic Principle", "Academic Principal"] },
  { key: "exec", label: "المدير التنفيذي", short: "تنفيذي", color: "#2E93CC", titles: ["Executive Manager", "Executive Director"] },
  { key: "hod", label: "رئيس القسم", short: "رئيس قسم", color: "#E0704F", titles: ["Head of Department", "HOD", "Head of Department (HOD)"] },
  { key: "tch", label: "المعلم", short: "معلم", color: "#E0A32B", titles: ["Teacher"] },
  { key: "std", label: "الطالب", short: "طلبة", color: "#22B07D", titles: ["Student"] },
  { key: "qua", label: "مسؤول الجودة", short: "جودة", color: "#4FC3D9", titles: ["Quality Officer", "Quality"] },
];

// A field is flagged as a weak point when its evaluator-weighted average drops below this.
const WEAK_THRESHOLD = 70;

// Palette lifted from the design (used for text/borders/backgrounds that depend on a score band).
const COLORS = {
  exc: "#22B07D",
  good: "#2E93CC",
  mid: "#E0A32B",
  low: "#D8543F",
  std: "#22B07D",
  oth: "#2E93CC",
};

/* =====================================================================
   HELPERS
   ===================================================================== */

const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const ar = (n) => String(Math.round(n)).replace(/[0-9]/g, (d) => AR_DIGITS[+d]);
const esc = (s) => String(s ?? "");
const bandColor = (s) => (s >= 90 ? COLORS.exc : s >= 80 ? COLORS.good : s >= 70 ? COLORS.mid : COLORS.low);
const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

const employeeFullName = (employee) =>
  [employee?.first_name, employee?.middle_name, employee?.last_name].filter(Boolean).join(" ") || "بدون اسم";

function sourceKeyForRoleTitle(title) {
  if (!title) return null;
  const t = String(title).trim().toLowerCase();
  const found = SOURCES.find((s) => s.titles.some((x) => x.toLowerCase() === t));
  return found ? found.key : null;
}

// Weighted average of a list of {value, weight} items. Falls back to a plain
// mean if weights are missing/zero so a bad weight config never divides by 0.
function weightedAverage(items, valueKey, weightKey = "weight") {
  const totalWeight = items.reduce((sum, it) => sum + (Number(it[weightKey]) || 0), 0);
  if (!totalWeight) return mean(items.map((it) => it[valueKey]));
  return items.reduce((sum, it) => sum + it[valueKey] * (Number(it[weightKey]) || 0), 0) / totalWeight;
}

/**
 * Cascades ONE evaluator's raw answers up to a score-per-field (0-100).
 *   question %   = score / max_score * 100
 *   sub_field %  = weighted avg of question % by questionResult.weight
 *   field %      = weighted avg of sub_field % by sub_field.weight
 * Returns [{ fieldId, label, score }]
 */
function computeSubmissionFieldScores(results) {
  const bySubField = {};

  (results || []).forEach((r) => {
    const qr = r.questionResult;
    if (!qr || !qr.sub_field || !qr.sub_field.field) return;
    const sf = qr.sub_field;
    const pct = qr.max_score ? (r.score / qr.max_score) * 100 : 0;

    if (!bySubField[sf.id]) bySubField[sf.id] = { weight: sf.weight, field: sf.field, items: [] };
    bySubField[sf.id].items.push({ pct, weight: qr.weight });
  });

  const subFieldScores = Object.values(bySubField).map((sf) => ({
    field: sf.field,
    weight: sf.weight,
    score: weightedAverage(sf.items, "pct", "weight"),
  }));

  const byField = {};
  subFieldScores.forEach((sf) => {
    const f = sf.field;
    if (!byField[f.id]) byField[f.id] = { field: f, items: [] };
    byField[f.id].items.push({ score: sf.score, weight: sf.weight });
  });

  return Object.values(byField).map((f) => ({
    fieldId: f.field.id,
    label: f.field.ar_name || f.field.en_name,
    score: weightedAverage(f.items, "score", "weight"),
  }));
}

/**
 * Turns the raw `dashboard` array (one entry per evaluator submission) into
 * everything the UI needs: KPI numbers, donut/source breakdown, the
 * field x source weakness matrix, and the student-vs-others gap list.
 */
function buildStats(dashboard) {
  if (!dashboard || !dashboard.length) return null;

  const nBySource = {};
  const avgScoresBySource = {};
  const formsBySource = {};
  SOURCES.forEach((s) => {
    nBySource[s.key] = 0;
    avgScoresBySource[s.key] = [];
    formsBySource[s.key] = [];
  });

  // fieldId -> { label, bySource: { sourceKey: [score, score, ...] } }
  const fields = {};

  dashboard.forEach((entry) => {
    // Students have no Employee record (so no role.title) — treat those evaluators as students.
    const isStudent = entry?.employee && !entry.employee.employee;
    const roleTitle = entry?.employee?.employee?.role?.title;
    const key = isStudent ? "std" : sourceKeyForRoleTitle(roleTitle);
    if (!key) {
      if (roleTitle) {
        // eslint-disable-next-line no-console
        console.warn(`EduEnvDashboard: role.title "${roleTitle}" doesn't match any SOURCES entry — add it to the mapping.`);
      }
      return;
    }

    nBySource[key] += 1;
    if (typeof entry.avg_score === "number") avgScoresBySource[key].push(entry.avg_score);

    formsBySource[key].push({
      id: entry.id ?? `${key}-${formsBySource[key].length}`,
      name: employeeFullName(entry.employee?.employee || entry.employee?.student),
      score: Number(entry.avg_score) || 0,
      date: entry.createdAt || entry.created_at || null,
    });

    computeSubmissionFieldScores(entry.results).forEach((fs) => {
      if (!fields[fs.fieldId]) fields[fs.fieldId] = { label: fs.label, bySource: {} };
      if (!fields[fs.fieldId].bySource[key]) fields[fs.fieldId].bySource[key] = [];
      fields[fs.fieldId].bySource[key].push(fs.score);
    });
  });

  const total = SOURCES.reduce((sum, s) => sum + nBySource[s.key], 0);
  if (!total) return null;

  // Average of the API's own per-submission avg_score, per source (used for the source bars card).
  const sourceAvg = {};
  SOURCES.forEach((s) => {
    sourceAvg[s.key] = Math.round(mean(avgScoresBySource[s.key]));
  });

  // Per field: score per source (mean across that source's submissions) + evaluator-weighted average.
  const domainRows = Object.entries(fields).map(([fieldId, f]) => {
    const bySourceAvg = {};
    SOURCES.forEach((s) => {
      const arr = f.bySource[s.key] || [];
      bySourceAvg[s.key] = arr.length ? mean(arr) : null;
    });

    const present = SOURCES.filter((s) => bySourceAvg[s.key] !== null);
    const weightedSum = present.reduce((sum, s) => sum + bySourceAvg[s.key] * nBySource[s.key], 0);
    const weightedN = present.reduce((sum, s) => sum + nBySource[s.key], 0);
    const wavg = weightedN ? weightedSum / weightedN : 0;

    return { fieldId, label: f.label, bySourceAvg, wavg: Math.round(wavg) };
  });

  domainRows.sort((a, b) => a.wavg - b.wavg); // weakest first

  const overallW = domainRows.length ? Math.round(mean(domainRows.map((d) => d.wavg))) : 0;
  const weakCount = domainRows.filter((d) => d.wavg < WEAK_THRESHOLD).length;
  const worst = domainRows[0] || null;

  // Student gap vs. mean of the other five sources, per field.
  const gaps = domainRows
    .map((d) => {
      const studentScore = d.bySourceAvg.std;
      const others = SOURCES.filter((s) => s.key !== "std" && d.bySourceAvg[s.key] !== null).map((s) => d.bySourceAvg[s.key]);
      if (studentScore === null || !others.length) return null;
      const othersAvg = mean(others);
      return {
        label: d.label,
        student: Math.round(studentScore),
        others: Math.round(othersAvg),
        gap: Math.round(othersAvg - studentScore),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.gap - a.gap);

  return { total, nBySource, sourceAvg, domainRows, overallW, weakCount, worst, gaps, formsBySource };
}

/* =====================================================================
   PRESENTATIONAL SUB-COMPONENTS
   ===================================================================== */

function Donut({ total, nBySource }) {
  const R = 42;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <svg width="112" height="112" viewBox="0 0 112 112" role="img" aria-label="مخطط دائري لتوزيع المقيِّمين على المصادر الستة">
      <g transform="translate(56,56) rotate(-90)">
        {SOURCES.map((s) => {
          const n = nBySource[s.key] || 0;
          const len = total ? (n / total) * C : 0;
          const dasharray = `${Math.max(len - 2, 0.5)} ${C - len + 2}`;
          const el = (
            <circle key={s.key} r={R} fill="none" stroke={s.color} strokeWidth="15" strokeDasharray={dasharray} strokeDashoffset={-offset} />
          );
          offset += len;
          return el;
        })}
      </g>
      <text x="56" y="54" textAnchor="middle" fill="#EAF1FA" fontSize="17" fontWeight="600">
        {ar(total)}
      </text>
      <text x="56" y="67" textAnchor="middle" fill="#6C82A3" fontSize="8">
        مقيِّم
      </text>
    </svg>
  );
}

function EduEnvDashboard({ orgId, year, month, onClose }) {
  const [fetching, setFetching] = useState(true);
  const [dashboard, setDashboard] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setFetching(true);
        const response = await fetchWisdomEduEnvDashboard(orgId, year, month);
        console.log(response)
        setDashboard(response || []);
        setSelectedSource(null);
      } catch (err) {
        console.error("error:", err);
      } finally {
        setFetching(false);
      }
    };

    load();
  }, [orgId, year, month]);

  const stats = useMemo(() => buildStats(dashboard), [dashboard]);

  const selectedSourceObj = selectedSource ? SOURCES.find((s) => s.key === selectedSource) : null;
  const selectedForms = selectedSourceObj && stats
    ? (stats.formsBySource[selectedSourceObj.key] || []).slice().sort((a, b) => b.score - a.score)
    : [];

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-[rgba(5,10,20,0.62)] p-4"
      style={{ fontFamily: '"Segoe UI", Tahoma, "Noto Sans Arabic", Arial, sans-serif' }}
    >
      <div
        dir="rtl"
        className="relative flex w-full max-w-[660px] max-h-[min(92vh,760px)] flex-col overflow-hidden rounded-2xl border border-[#33507E] bg-[#0F1A2B] text-[#EAF1FA] shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
        role="dialog"
        aria-modal="true"
        aria-label="لوحة تحليل تقييم البيئة التعليمية"
      >
        <h1 className="sr-only">لوحة تحليل تقييم البيئة التعليمية عبر ستة مصادر وستة مجالات</h1>

        <div className="flex flex-none items-center gap-2 border-b border-[#26385A] bg-[#16233A] px-3.5 py-2.5">
          <div>
            <h1 className="m-0 text-sm font-semibold leading-snug">
              تحليل تقييم البيئة التعليمية
              <span className="ms-1 inline-block rounded-full border border-[rgba(224,163,43,0.36)] bg-[rgba(224,163,43,0.14)] px-2 py-0.5 align-[1px] text-[9.5px] text-[#F2D69A]">
                المجالات مبدئية
              </span>
            </h1>
            <div className="mt-0.5 text-[10.5px] text-[#6C82A3]">
              {[year, month].filter(Boolean).length ? `${month ? month + " / " : ""}${year || ""}` : "كل الفترات"}
            </div>
          </div>
          <div className="ms-auto flex flex-none gap-1.5">
            <button
              type="button"
              aria-label="إغلاق"
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-lg border border-[#26385A] text-sm text-[#6C82A3] transition-colors hover:bg-[#1C2C47] hover:text-[#EAF1FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E93CC] focus-visible:ring-offset-1"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 pb-3.5 pt-2.5 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[#33507E]">
          {fetching && <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">جارِ تحميل البيانات...</div>}

          {!fetching && !stats && <div className="px-2 py-6 text-center text-xs text-[#6C82A3]">لا توجد بيانات كافية لعرض التحليل.</div>}

          {!fetching && stats && (
            <>
              <div className="mb-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                <div className="rounded-lg border border-[#26385A] bg-[#16233A] px-2 py-1.5 text-center">
                  <span className="block text-[9.5px] leading-tight text-[#6C82A3]">المقيِّمون</span>
                  <b className="block text-[17px] font-semibold leading-tight tabular-nums">{ar(stats.total)}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#16233A] px-2 py-1.5 text-center">
                  <span className="block text-[9.5px] leading-tight text-[#6C82A3]">المجالات</span>
                  <b className="block text-[17px] font-semibold leading-tight tabular-nums">{ar(stats.domainRows.length)}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#16233A] px-2 py-1.5 text-center">
                  <span className="block text-[9.5px] leading-tight text-[#6C82A3]">المتوسط المرجَّح</span>
                  <b className="block text-[17px] font-semibold leading-tight tabular-nums" style={{ color: bandColor(stats.overallW) }}>
                    {ar(stats.overallW)}٪
                  </b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#16233A] px-2 py-1.5 text-center">
                  <span className="block text-[9.5px] leading-tight text-[#6C82A3]">مجالات تحت ٧٠٪</span>
                  <b className="block text-[17px] font-semibold leading-tight tabular-nums" style={{ color: COLORS.low }}>
                    {ar(stats.weakCount)}
                  </b>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[minmax(0,158px)_minmax(0,1fr)]">
                <section className="mb-2.5 rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5 sm:mb-0">
                  <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">نسبة كل مصدر</h2>
                  <div className="flex flex-col items-center gap-1.5">
                    <Donut total={stats.total} nBySource={stats.nBySource} />
                    <div className="flex w-full flex-col gap-0.5">
                      {SOURCES.map((s) => {
                        const n = stats.nBySource[s.key] || 0;
                        const pct = stats.total ? Math.round((n / stats.total) * 100) : 0;
                        return (
                          <div className="grid grid-cols-[8px_1fr_auto] items-center gap-1 text-[9.5px]" key={s.key}>
                            <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
                            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[#9DB2CE]">{s.label}</span>
                            <span className="font-semibold tabular-nums text-[#EAF1FA]">
                              {ar(pct)}٪ <u className="text-[9px] font-normal text-[#6C82A3] no-underline">({ar(n)})</u>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>

                <section className="rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
                  <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
                    متوسط تقييم البيئة التعليمية حسب المصدر{" "}
                    <em className="text-[10px] font-normal not-italic text-[#6C82A3]">وعدد المقيِّمين — اضغط لعرض النماذج</em>
                  </h2>
                  <div className="flex flex-col gap-1.5">
                    {SOURCES.slice()
                      .sort((a, b) => stats.sourceAvg[b.key] - stats.sourceAvg[a.key])
                      .map((s) => {
                        const isSel = selectedSource === s.key;
                        return (
                          <div
                            key={s.key}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedSource(isSel ? null : s.key)}
                            className={`-m-1 cursor-pointer rounded-md border p-1 ${isSel ? "border-[#33507E] bg-[#1C2C47]" : "border-transparent hover:bg-[#1C2C47]/60"}`}
                          >
                            <div className="mb-0.5 flex items-baseline gap-1.5 text-[10px]">
                              <b className="text-[10.5px] font-medium text-[#EAF1FA]">{s.label}</b>
                              <u className="text-[9px] text-[#6C82A3] no-underline">{ar(stats.nBySource[s.key] || 0)} مقيِّم</u>
                              <i className="ms-auto text-[11.5px] font-semibold not-italic tabular-nums" style={{ color: s.color }}>
                                {ar(stats.sourceAvg[s.key] || 0)}٪
                              </i>
                            </div>
                            <span className="block h-2 overflow-hidden rounded-l-[3px] bg-[#1C2C47]">
                              <i
                                className="block h-full rounded-l-[3px] transition-[width] duration-500 [transition-timing-function:cubic-bezier(0.2,0.8,0.25,1)] motion-reduce:transition-none"
                                style={{ width: `${stats.sourceAvg[s.key] || 0}%`, background: s.color }}
                              />
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </section>
              </div>

              <section className="mb-2.5 rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
                <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
                  نقاط الضعف حسب مجالات البيئة التعليمية{" "}
                  <em className="text-[10px] font-normal not-italic text-[#6C82A3]">المتوسط مرجَّح بعدد المقيِّمين</em>
                </h2>

                {stats.worst && (
                  <div className="mb-2.5 rounded-lg border border-[rgba(216,84,63,0.34)] bg-[rgba(216,84,63,0.10)] px-2.5 py-2 text-[10px] leading-relaxed text-[#F0B4AA]">
                    <b className="mb-0.5 block font-semibold text-[#FFD3CA]">
                      أضعف مجال: {stats.worst.label} بمتوسط مرجَّح {ar(stats.worst.wavg)}٪
                    </b>
                    {ar(stats.weakCount)} من {ar(stats.domainRows.length)} مجالات تحت {ar(WEAK_THRESHOLD)}٪ بعد الترجيح بعدد المقيِّمين.
                  </div>
                )}

                <table className="w-full table-fixed border-separate [border-spacing:2.5px]">
                  <thead>
                    <tr>
                      <th className="w-[62px] pb-1 text-right text-[7px] font-medium leading-tight text-[#6C82A3] sm:w-[100px] sm:text-[8px]">
                        المجال
                      </th>
                      {SOURCES.map((s) => (
                        <th key={s.key} className="pb-1 text-center text-[7px] font-medium leading-tight text-[#6C82A3] sm:text-[8px]">
                          {s.short}
                        </th>
                      ))}
                      <th className="w-[30px] pb-1 text-center text-[7px] font-medium leading-tight text-[#9DB2CE] sm:w-[38px] sm:text-[8px]">
                        مرجَّح
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.domainRows.map((d) => (
                      <tr key={d.fieldId}>
                        <td className="pe-1 text-right text-[7.6px] leading-snug text-[#EAF1FA] sm:text-[8.8px]">{esc(d.label)}</td>
                        {SOURCES.map((s) => {
                          const v = d.bySourceAvg[s.key];
                          if (v === null) {
                            return (
                              <td key={s.key}>
                                <div className="flex h-6 items-center justify-center rounded-md bg-[#1C2C47] text-[9px] font-semibold tabular-nums text-[#6C82A3] sm:h-7 sm:text-[10.5px]">
                                  —
                                </div>
                              </td>
                            );
                          }
                          const rounded = Math.round(v);
                          const c = bandColor(rounded);
                          return (
                            <td key={s.key}>
                              <div
                                className={`flex h-6 items-center justify-center rounded-md text-[9px] font-semibold tabular-nums sm:h-7 sm:text-[10.5px]${
                                  rounded < WEAK_THRESHOLD ? " shadow-[inset_0_0_0_1.5px_#D8543F]" : ""
                                }`}
                                style={{ background: `${c}22`, color: c }}
                              >
                                {ar(rounded)}
                              </div>
                            </td>
                          );
                        })}
                        <td>
                          <div
                            className="flex h-6 items-center justify-center rounded-md border border-[#33507E] bg-[#1C2C47] text-[9px] font-semibold tabular-nums sm:h-7 sm:text-[10.5px]"
                            style={{ color: bandColor(d.wavg) }}
                          >
                            {ar(d.wavg)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-2 flex flex-wrap gap-2 text-[9.5px] text-[#9DB2CE]">
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.exc }} />٩٠٪+
                  </span>
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.good }} />
                    ٨٠–٨٩٪
                  </span>
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.mid }} />
                    ٧٠–٧٩٪
                  </span>
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.low }} />
                    أقل من ٧٠٪ · نقطة ضعف
                  </span>
                  <span>
                    <u className="text-[#6C82A3] no-underline">مرتبة تصاعدياً — الأضعف أولاً</u>
                  </span>
                </div>
              </section>

              <section className="rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
                <h2 className="m-0 mb-2 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-[#9DB2CE]">
                  فجوة الطالب <em className="text-[10px] font-normal not-italic text-[#6C82A3]">الطلبة مقابل متوسط المصادر الخمسة الأخرى</em>
                </h2>
                <div>
                  {stats.gaps.map((g) => {
                    const a = Math.min(g.others, g.student);
                    const b = Math.max(g.others, g.student);
                    return (
                      <div
                        className="grid grid-cols-[84px_1fr_30px] items-center gap-1.5 rounded-md p-0.5 hover:bg-[#1C2C47] sm:grid-cols-[112px_1fr_32px]"
                        title={esc(g.label)}
                        key={g.label}
                      >
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[9.5px] text-[#9DB2CE]">{esc(g.label)}</span>
                        <span className="relative h-[13px]">
                          <i
                            className="absolute top-1.5 h-0.5 rounded-sm bg-[#33507E]"
                            style={{ right: `${a}%`, width: `${b - a}%` }}
                          />
                          <i
                            className="absolute top-[3px] h-2 w-2 translate-x-1/2 rounded-full border-[1.5px] border-[#16233A]"
                            style={{ right: `${g.student}%`, background: COLORS.std }}
                          />
                          <i
                            className="absolute top-[3px] h-2 w-2 translate-x-1/2 rounded-full border-[1.5px] border-[#16233A]"
                            style={{ right: `${g.others}%`, background: COLORS.oth }}
                          />
                        </span>
                        <span
                          className="text-left text-[10px] font-semibold tabular-nums"
                          style={{ color: g.gap >= 12 ? COLORS.low : g.gap >= 7 ? COLORS.mid : "#6C82A3" }}
                        >
                          {g.gap > 0 ? "+" : ""}
                          {ar(g.gap)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 grid grid-cols-[84px_1fr_30px] gap-1.5 sm:grid-cols-[112px_1fr_32px]">
                  <div />
                  <div className="relative h-3 border-t border-[#26385A]">
                    {[0, 50, 100].map((p) => (
                      <span className="absolute top-0 translate-x-1/2 text-[9px] text-[#6C82A3]" style={{ right: `${p}%` }} key={p}>
                        {ar(p)}٪
                      </span>
                    ))}
                  </div>
                  <div />
                </div>
                <div className="mt-1.5 flex flex-wrap gap-2 text-[9.5px] text-[#9DB2CE]">
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.oth }} />
                    متوسط المصادر الأخرى
                  </span>
                  <span className="flex items-center gap-1">
                    <b className="block h-[7px] w-[7px] rounded-sm" style={{ background: COLORS.std }} />
                    الطلبة
                  </span>
                  <span>
                    <u className="text-[#6C82A3] no-underline">موجب = الطلبة يقيّمون المجال أدنى</u>
                  </span>
                </div>
              </section>

              {selectedSourceObj && (
                <section className="mt-2.5 rounded-xl border border-[#26385A] bg-[#16233A] px-2.5 py-2.5">
                  <div className="mb-2 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-sm" style={{ background: selectedSourceObj.color }} />
                    <h2 className="m-0 text-[11.5px] font-semibold text-[#9DB2CE]">
                      نماذج {selectedSourceObj.label}
                      <em className="ms-1 text-[10px] font-normal not-italic text-[#6C82A3]">({ar(selectedForms.length)} نموذج)</em>
                    </h2>
                    <button
                      type="button"
                      onClick={() => setSelectedSource(null)}
                      className="ms-auto text-[10px] text-[#6C82A3] hover:text-[#EAF1FA]"
                    >
                      إغلاق ✕
                    </button>
                  </div>

                  <div className="flex max-h-[220px] flex-col gap-1 overflow-y-auto pe-1">
                    {selectedForms.map((f) => {
                      const d = f.date ? new Date(f.date) : null;
                      const dateLabel = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("ar-EG") : "—";
                      const c = bandColor(f.score);
                      return (
                        <div
                          key={f.id}
                          className="grid grid-cols-[1fr_auto_50px] items-center gap-2 rounded-md border border-[#26385A] bg-[#1C2C47] px-2 py-1.5"
                        >
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[10.5px] text-[#EAF1FA]">{esc(f.name)}</span>
                          <span className="text-[9.5px] text-[#6C82A3]">{dateLabel}</span>
                          <span
                            className="rounded-md py-0.5 text-center text-[10.5px] font-semibold tabular-nums"
                            style={{ background: `${c}22`, color: c }}
                          >
                            {ar(Math.round(f.score))}٪
                          </span>
                        </div>
                      );
                    })}
                    {!selectedForms.length && (
                      <p className="py-4 text-center text-[10.5px] text-[#6C82A3]">لا توجد نماذج لهذا المصدر.</p>
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EduEnvDashboard;